import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertCircle } from "lucide-react";
import profileService from "@/services/profileService";

/**
 * Modal para criar ou editar perfil de acesso e seus menus
 */
export default function ProfileFormModal({
  open,
  onOpenChange,
  profile,
  menus = [],
  onSave,
  loading,
}) {
  const isEditing = !!profile;
  const isSystem = !!profile?.system;

  const [formData, setFormData] = useState({ name: "", menus: [] });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (profile) {
      setFormData({
        name: profile.name || "",
        menus: profile.menus || [],
      });
    } else {
      setFormData({ name: "", menus: [] });
    }
    setErrors({});
  }, [profile, open]);

  const handleNameChange = (value) => {
    setFormData((prev) => ({ ...prev, name: value }));
    if (errors.name) {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handleToggleMenu = (link, checked) => {
    setFormData((prev) => ({
      ...prev,
      menus: checked
        ? [...prev.menus, link]
        : prev.menus.filter((m) => m !== link),
    }));
  };

  const allSelected =
    menus.length > 0 && menus.every((m) => formData.menus.includes(m.link));

  const handleToggleAll = () => {
    setFormData((prev) => ({
      ...prev,
      menus: allSelected ? [] : menus.map((m) => m.link),
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const newErrors = profileService.validateProfileData(formData);
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) {
      return;
    }

    // Mantém a ordem dos menus do painel
    const orderedMenus = menus
      .map((m) => m.link)
      .filter((link) => formData.menus.includes(link));
    // Links vinculados que não estão mais em /menus/all são preservados
    const unknownMenus = formData.menus.filter(
      (link) => !menus.some((m) => m.link === link),
    );

    onSave({
      name: profileService.normalizeName(formData.name),
      menus: [...orderedMenus, ...unknownMenus],
    });
  };

  const normalizedName = profileService.normalizeName(formData.name);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? "Editar Perfil" : "Novo Perfil"}
          </DialogTitle>
          <DialogDescription>
            Defina o nome do perfil e quais menus ele pode acessar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Nome */}
          <div className="space-y-2">
            <Label htmlFor="profile-name">
              Nome do perfil <span className="text-red-500">*</span>
            </Label>
            <Input
              id="profile-name"
              placeholder="Ex.: atendente"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              className={errors.name ? "border-red-500" : ""}
              disabled={loading || isSystem}
            />
            {errors.name ? (
              <div className="flex items-center gap-1 text-red-500 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.name}</span>
              </div>
            ) : isSystem ? (
              <p className="text-xs text-gray-500">
                Perfil do sistema: o nome não pode ser alterado.
              </p>
            ) : (
              normalizedName && (
                <p className="text-xs text-gray-500">
                  Será salvo como <code>{normalizedName}</code>
                </p>
              )
            )}
          </div>

          {/* Menus */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Menus com acesso</Label>
              {menus.length > 0 && (
                <button
                  type="button"
                  onClick={handleToggleAll}
                  className="text-xs font-medium text-amber-600 hover:text-amber-700"
                  disabled={loading}
                >
                  {allSelected ? "Desmarcar todos" : "Selecionar todos"}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 rounded-md border border-gray-200 p-3">
              {menus.length === 0 ? (
                <p className="text-sm text-gray-500 sm:col-span-2">
                  Nenhum menu cadastrado.
                </p>
              ) : (
                menus.map((menu) => (
                  <label
                    key={menu.link}
                    htmlFor={`menu-${menu.link}`}
                    className="flex items-center gap-2 cursor-pointer text-sm"
                  >
                    <Checkbox
                      id={`menu-${menu.link}`}
                      checked={formData.menus.includes(menu.link)}
                      onCheckedChange={(checked) =>
                        handleToggleMenu(menu.link, checked === true)
                      }
                      disabled={loading}
                    />
                    <span className="text-gray-900">{menu.label}</span>
                    {menu.enabled === false && (
                      <span className="text-xs text-gray-400">(inativo)</span>
                    )}
                  </label>
                ))
              )}
            </div>
            <p className="text-xs text-gray-500">
              {formData.menus.length} menu(s) selecionado(s)
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={loading}
              style={{ background: "#1b5e20" }}
              onMouseEnter={(e) =>
                !loading && (e.target.style.background = "#144919")
              }
              onMouseLeave={(e) =>
                !loading && (e.target.style.background = "#1b5e20")
              }
            >
              {loading ? "Salvando..." : isEditing ? "Atualizar" : "Criar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
